import {
  FiUsers as UsersIcon,
  FiUser as UserIcon,
  FiActivity as ProtocolIcon,
  FiCpu as DeviceIcon,
  FiSettings as SettingsIcon,
  FiSliders as FeedbackResponseIcon,
  FiClipboard as QuestionnaireIcon,
  FiBarChart2 as AssessmentIcon,
  FiLayers as SessionIcon,
  FiHome as DashboardIcon,
} from 'react-icons/fi';
import { User } from '../types/user';
import { Tag } from '../types/tags';
import { ProtocolCategory } from '../types/protocols';
import { Category as QuestionnaireCategory } from '../types/questionnaire';
import { Category as AssessmentCategory } from '../types/assessment';

export const rolesPermission = (
  user: User,
  allowedRoles: number,
  ignoreSuperUser: boolean = false
): boolean => {
  // allowedRoles is a number argument that grabs the each item on its corresponding index, ex. 13 will grab ['AccountAdmin', 'Coach']
  // you never need to add 0 as the code is built to check for SuperUser roles always

  // if no groups on the user object return false
  if (!user || !Array.isArray(user.groups)) {
    return false;
  }

  if (!ignoreSuperUser && user.groups.some((item) => item.name === 'SuperUser')) {
    return true;
  }

  const permissedRoles = allowedRoles.toString().split('').map(Number);
  let roles = ['SuperUser', 'AccountAdmin', 'Therapist', 'Coach', 'ReadOnly', 'LiveOnly'];
  let tempRoles = [];

  permissedRoles.map((num) => {
    tempRoles.push(roles[num]);
  });

  if (user.groups.some((item) => tempRoles.includes(item.name))) {
    return true;
  }

  return false;
};

export interface ProtocolPermissionArgs {
  user: User;
  data: { therapistId?: string; userId?: string };
  isQuestionnaire?: boolean;
}

export const protocolPermission = ({
  user,
  data,
  isQuestionnaire = false,
}: ProtocolPermissionArgs): boolean => {
  // if no groups on the user object return false
  if (!user || !Array.isArray(user.groups)) {
    return false;
  }

  if (user.groups.some((item) => item.name === 'SuperUser')) {
    return true;
  }

  if (user.userId === data?.therapistId) {
    return true;
  }

  if (isQuestionnaire && user.userId === data.userId) {
    return true;
  }

  return false;
};

export const getLinks = (
  user: User
): Array<{
  title: string;
  icon: any;
  href: string;
  new?: boolean;
}> => {
  // if no groups on the user object return empty
  if (!user || !Array.isArray(user.groups)) {
    return [];
  }

  const links = [
    {
      title: 'Dashboard',
      icon: DashboardIcon,
      href: '/app/dashboard',
    },
    {
      title: 'Clients',
      icon: UsersIcon,
      href: '/app/clients',
    },
    {
      title: 'Operators',
      icon: UserIcon,
      href: '/app/users',
      new: true,
    },
    {
      title: 'Questionnaires',
      icon: QuestionnaireIcon,
      href: '/app/questionnaires',
    },
    {
      title: 'Track Devices',
      icon: DeviceIcon,
      href: '/app/devices',
    },
    {
      title: 'Feedback Responses',
      icon: FeedbackResponseIcon,
      href: '/app/feedbackresponses',
    },
    {
      title: 'Protocols',
      icon: ProtocolIcon,
      href: '/app/protocols',
    },
    {
      title: 'Assessments',
      icon: AssessmentIcon,
      href: '/app/assessments',
    },
    {
      title: 'Sessions',
      icon: SessionIcon,
      href: '/app/sessions',
    },
    {
      title: 'Settings',
      icon: SettingsIcon,
      href: '/app/account',
    },
  ];

  if (user.groups.some((item) => item.name === 'SuperUser')) {
    return links;
  }

  let tempArr = [];
  const roleLinks = {
    AccountAdmin: ['Dashboard', 'Clients', 'Operators', 'Devices', 'Settings'],
    Therapist: [
      'Dashboard',
      'Clients',
      'Questionnaires',
      'Track Devices',
      'Feedback Responses',
      'Protocols',
      'Assessments',
      'Sessions',
      'Settings',
    ],
    Coach: [
      'Dashboard',
      'Clients',
      'Questionnaires',
      'Track Devices',
      'Feedback Responses',
      'Protocols',
      'Assessments',
      'Sessions',
      'Settings',
    ],
    ReadOnly: [
      'Dashboard',
      'Clients',
      'Questionnaires',
      'Feedback Responses',
      'Protocols',
      'Sessions',
    ],
    LiveOnly: ['Clients'],
  };

  user.groups.map((item) => {
    tempArr.push(roleLinks[item.name]);
  });

  // Flatten and reduce to unique items
  const uniqueLinks = Array.from(new Set(tempArr.flat()));

  // Build linksArr in the order of the original links array, only including those in uniqueLinks
  const linksArr = links.filter((link) => uniqueLinks.includes(link.title));

  return linksArr;
};

// Union type allows all existing usages. Assessment Category does not have therapistId as a string.
export type PermissionTag =
  | (Tag & { therapistId?: string })
  | (ProtocolCategory & { therapistId?: string | null })
  | (QuestionnaireCategory & { therapistId?: string })
  | AssessmentCategory;

export const tagPermission = (user: User, tag: PermissionTag): boolean => {
  // Block deletion for NMI/TrueBearing on objects that have therapistId
  if ('therapistId' in tag && (tag.therapistId === 'NMI' || tag.therapistId === 'TrueBearing')) {
    return false;
  }

  // SuperUser always allowed
  if (user.groups.some((item) => item.name === 'SuperUser')) return true;

  // If tag has a therapistId, check for user match
  if ('therapistId' in tag && user.userId === tag.therapistId) return true;

  return false;
};
